import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Product image uploads.
 * ----------------------
 * Every product image on PokeShani is served straight from the public Supabase
 * Storage bucket, and Supabase bills *Storage* bandwidth - "Cached Egress" is
 * literally the CDN cache hits for those files. On the Free plan the quota is
 * 5 GB per month, and it is easy to burn through it:
 *
 *   an unoptimised 4 MB phone photo displayed in a 260 px card costs 4 MB of
 *   quota on EVERY page view, by every visitor and every crawler.
 *
 * So the bytes have to be small *before* they reach the bucket. This module
 * resizes an image in the browser, re-encodes it as WebP and uploads it with a
 * one-year cache header, which is safe because the object name contains both a
 * timestamp and a random suffix (an object is therefore never modified).
 *
 * Supabase's own image transformations (the ?width= API) are Pro-plan only, so
 * doing this client-side is the only way to stay inside the Free quota.
 */

/** Bucket that holds thumbnails and additional product images. */
export const STORAGE_BUCKET = 'digitalproducts';

/** Longest edge kept for an uploaded image. Cards render far smaller. */
export const MAX_IMAGE_WIDTH = 1600;

/** Refuse absurd source files instead of uploading hundreds of megabytes. */
export const MAX_SOURCE_BYTES = 8 * 1024 * 1024;

/** Additional images per product. Long galleries multiply storage egress. */
export const MAX_ADDITIONAL_IMAGES = 8;

/** One year, in seconds: object names are unique, so the file never changes. */
export const UPLOAD_CACHE_CONTROL = '31536000';

/** WebP quality. 0.82 is visually indistinguishable at 1600 px. */
const WEBP_QUALITY = 0.82;

export const humanFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export const isAllowedImageFile = (file: File): boolean =>
  file.type.startsWith('image/');

export const isStorageUrl = (url?: string | null): boolean =>
  typeof url === 'string' && url.includes(`/object/public/${STORAGE_BUCKET}/`);

const loadBitmap = async (file: File): Promise<ImageBitmap | null> => {
  try {
    return await createImageBitmap(file);
  } catch {
    // Formats the browser cannot decode (HEIC on most desktop browsers, some
    // RAW/TIFF files). Better to upload the original than to block the creator.
    return null;
  }
};

/**
 * Resize + re-encode an image file. Returns the original file when shrinking is
 * not possible or would make the file bigger (never make things worse).
 */
export const optimizeImage = async (file: File): Promise<File> => {
  if (!isAllowedImageFile(file)) {
    throw new Error('Only image files can be uploaded (PNG, JPG, WebP, GIF).');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new Error(
      `Image is ${humanFileSize(file.size)} - the limit is ${humanFileSize(MAX_SOURCE_BYTES)}. Please pick a smaller file.`
    );
  }

  const bitmap = await loadBitmap(file);
  if (!bitmap) return file;

  const scale = Math.min(1, MAX_IMAGE_WIDTH / bitmap.width);
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }

  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/webp', WEBP_QUALITY)
  );

  // toBlob falls back to PNG on browsers without WebP encoding, so the
  // extension always follows the type the browser actually produced.
  if (!blob || blob.size >= file.size) return file;

  const extension =
    blob.type === 'image/webp' ? 'webp' : blob.type === 'image/jpeg' ? 'jpg' : 'png';
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';

  return new File([blob], `${baseName}.${extension}`, { type: blob.type });
};

export interface UploadedImage {
  /** Public URL to store in the database. */
  url: string;
  /** Object path inside the bucket. */
  path: string;
  /** The optimised file, e.g. for a local preview. */
  file: File;
  /** Bytes that will be transferred, and bytes the creator selected. */
  bytes: number;
  originalBytes: number;
}

const randomSuffix = (): string => Math.random().toString(36).slice(2, 8);

/** Strip anything from a file name that would make an awkward object path. */
const safeName = (name: string): string =>
  name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'image';

/**
 * Optimise, then upload to `folder` inside the product bucket.
 * A timestamp plus a random suffix guarantees a fresh path per upload (so two
 * files picked in the same millisecond never collide) and lets the object be
 * cached for a year.
 */
export const uploadOptimizedImage = async (
  supabase: SupabaseClient,
  folder: 'thumbnails' | 'additional-images',
  file: File
): Promise<UploadedImage> => {
  const optimized = await optimizeImage(file);
  const path = `${folder}/${Date.now()}-${randomSuffix()}-${safeName(optimized.name)}`;

  const { data, error } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(path, optimized, {
      cacheControl: UPLOAD_CACHE_CONTROL,
      contentType: optimized.type || 'image/webp',
      upsert: false
    });

  if (error) throw error;
  if (!data) throw new Error('Upload failed: Storage returned no object path.');

  const {
    data: { publicUrl }
  } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(data.path);

  return {
    url: publicUrl,
    path: data.path,
    file: optimized,
    bytes: optimized.size,
    originalBytes: file.size
  };
};

/** Turn a public Storage URL back into the object path used by the API. */
export const publicUrlToStoragePath = (url?: string | null): string | null => {
  if (!isStorageUrl(url)) return null;
  const marker = `/object/public/${STORAGE_BUCKET}/`;
  const text = String(url);
  const rawPath = text.slice(text.indexOf(marker) + marker.length).split('?')[0];
  try {
    return decodeURIComponent(rawPath);
  } catch {
    return rawPath;
  }
};

/**
 * Delete objects that are no longer referenced (e.g. an image the creator
 * replaced). Without this, every edit leaves another multi-megabyte orphan in
 * the bucket - which is what pushed the project towards the 1 GB storage cap.
 * Failures are logged, never thrown: a failed cleanup must not break a save.
 */
export const deleteStorageObjects = async (
  supabase: SupabaseClient,
  urls: Array<string | null | undefined>
): Promise<void> => {
  const paths = Array.from(
    new Set(urls.map(publicUrlToStoragePath).filter((path): path is string => Boolean(path)))
  );
  if (!paths.length) return;

  const { error } = await supabase.storage.from(STORAGE_BUCKET).remove(paths);
  if (error) console.warn('Could not remove replaced image(s):', error.message);
};
